/**
 * Destructive-confirm dialog for Shogun `platform_call` (app-shell ADR-019).
 *
 * The client-side `platformCallExecutor` gates destructive calls behind a
 * `confirm(preview) => Promise<boolean>` DOM action: approval is recorded ONLY
 * when the returned promise resolves `true`. This module supplies that callback.
 *
 *   - `useShogunConfirm()` returns the `confirm` callback (passed verbatim into
 *     `createPlatformCallExecutor`) plus the pending-preview state and the
 *     approve/deny resolvers the dialog wires to its buttons.
 *   - `ShogunConfirmDialog` renders the pending change preview with Approve /
 *     Deny buttons. Approve resolves the executor's promise `true` (execute);
 *     Deny resolves it `false` (declined — nothing executes).
 *
 * The confirm is non-blocking on the bridge side: the executor kicks it off
 * out-of-band and returns `confirmation_required` immediately, so the 9-minute
 * retryable bridge activity never waits on a human. The model cannot set
 * the approval — only this real DOM click can.
 */

import { useCallback, useRef, useState } from 'react';
import type { ExperienceMode } from '@localagent/sdk';
import type { ConfirmFn, PlatformCallPreview } from '../../shogun/platformCallExecutor';
import { namedTarget, NO_NAMED_TARGET } from '../chat/named-target';
import { PlatformCallArgumentsPreview } from './PlatformCallArgumentsPreview';
import { startsAProgramOnThisComputer } from '../../shogun/platform-call-preview';

interface PendingConfirm {
  preview: PlatformCallPreview;
  resolve: (approved: boolean) => void;
}

export { redactSecrets, SECRET_KEY_RE } from '../chat/redact-secrets';

export interface UseShogunConfirm {
  /** The `confirm` callback to hand to `createPlatformCallExecutor`. */
  confirm: ConfirmFn;
  /** The preview currently awaiting a decision, or null. */
  pending: PlatformCallPreview | null;
  /** Resolve the pending confirm as approved (executor promise → true). */
  approve: () => void;
  /** Resolve the pending confirm as declined (executor promise → false). */
  deny: () => void;
}

export function useShogunConfirm(): UseShogunConfirm {
  const [pending, setPending] = useState<PlatformCallPreview | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((preview) => {
    return new Promise<boolean>((resolve) => {
      // Only one confirm is shown at a time. If a prior one is still pending,
      // decline it (resolve false) before replacing — never leave a promise
      // dangling.
      if (pendingRef.current) {
        pendingRef.current.resolve(false);
      }
      pendingRef.current = { preview, resolve };
      setPending(preview);
    });
  }, []);

  const settle = useCallback((approved: boolean) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    p?.resolve(approved);
  }, []);

  const approve = useCallback(() => settle(true), [settle]);
  const deny = useCallback(() => settle(false), [settle]);

  return { confirm, pending, approve, deny };
}

export interface ShogunConfirmDialogProps {
  pending: PlatformCallPreview | null;
  onApprove: () => void;
  onDeny: () => void;
  mode?: ExperienceMode;
}

export function ShogunConfirmDialog({ pending, onApprove, onDeny, mode }: ShogunConfirmDialogProps) {
  if (!pending) return null;
  const isNormal = mode !== 'advanced';
  const target = namedTarget(pending.arguments);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shogun-confirm-title"
    >
      <div
        data-testid="shogun-confirm-dialog"
        className="w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-2xl"
      >
        <h2 id="shogun-confirm-title" className="text-body-reading font-semibold text-foreground">
          Confirm destructive action
        </h2>
        {isNormal ? (
          <p className="mt-1 text-label text-muted-foreground">
            {target ? (
              <>
                Shogun wants to continue with{' '}
                <span data-testid="shogun-confirm-target">{target}</span>. This change is not
                automatically reversible.
              </>
            ) : (
              <span data-testid="shogun-confirm-no-target">{NO_NAMED_TARGET}</span>
            )}
          </p>
        ) : null}
        {isNormal && startsAProgramOnThisComputer(pending.resource, pending.method) && (
          <PlatformCallArgumentsPreview
            resource={pending.resource}
            method={pending.method}
            args={pending.arguments}
            testId="shogun-confirm-preview"
            className="mt-3 max-h-48 overflow-auto rounded-md bg-muted p-3 text-caption"
          />
        )}
        {isNormal ? null : (
          <>
            <p className="mt-1 text-body text-muted-foreground">
              Shogun wants to run{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-caption">
                {pending.resource}.{pending.method}
              </code>
              . This change is not automatically reversible.
            </p>
            <PlatformCallArgumentsPreview
              resource={pending.resource}
              method={pending.method}
              args={pending.arguments}
              testId="shogun-confirm-preview"
              className="mt-3 max-h-48 overflow-auto rounded-md bg-muted p-3 text-caption"
            />
          </>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            data-testid="shogun-confirm-deny"
            onClick={onDeny}
            className="rounded-md bg-secondary px-3 py-1.5 text-body text-secondary-foreground transition-colors hover:bg-secondary/80"
          >
            Deny
          </button>
          <button
            data-testid="shogun-confirm-approve"
            onClick={onApprove}
            className="rounded-md bg-destructive px-3 py-1.5 text-label font-medium text-destructive-foreground transition-colors hover:bg-destructive/90"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
